import { useSetAtom } from 'jotai';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { pendingCheckoutPlanAtom } from '@/components/Modals/SignUp/atoms';
import { conversionPoints, ModalRoutingProvider } from '@/components/Modals/context/ModalRoutingContext';
import type { AccountModalNames, ModalConversionPoints } from '@/components/Modals/shared-types';
import { recordConversion } from '@/utils/analytics/conversion';
import type { AccountModalsContextType, ModalProps } from '@/components/Modals/shared-types';
const AccountModalsContext = createContext<AccountModalsContextType | null>(null);
const AccountModalsProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [currentModal, setCurrentModalState] = useState<{
    modal: AccountModalNames | null;
    screen?: string;
    props?: ModalProps;
  }>({
    modal: null
  });
  const setPendingCheckoutPlan = useSetAtom(pendingCheckoutPlanAtom);

  // Closing the auth modal ends whatever purchase it interrupted, so the
  // pending checkout plan dies with it — consumed by a successful login, or
  // dropped here on abandonment (DEV-6836).
  const handleOpenChange = useCallback(() => {
    setCurrentModalState({
      modal: null
    });
    setPendingCheckoutPlan(null);
  }, [setPendingCheckoutPlan]);
  const setCurrentModal = useCallback((modal: AccountModalNames, screen?: string, props?: ModalProps) => {
    setCurrentModalState({
      modal,
      screen,
      props
    });
  }, []);
  const providerValue = useMemo(() => ({
    setCurrentModal: (modal: AccountModalNames, screen?: string, props?: ModalProps) => {
      setCurrentModal(modal, screen, props);
    },
    currentModal: currentModal.modal,
    initialScreen: currentModal.screen,
    modalProps: currentModal.props,
    handleOpenChange
  }), [currentModal.modal, currentModal.screen, currentModal.props, setCurrentModal, handleOpenChange]);
  return <AccountModalsContext.Provider value={providerValue} data-sentry-element="AccountModalsContext.Provider" data-sentry-component="AccountModalsProvider" data-sentry-source-file="context.tsx">
      <ModalRoutingProvider accountModalsContext={providerValue} data-sentry-element="ModalRoutingProvider" data-sentry-source-file="context.tsx">
        {children}
      </ModalRoutingProvider>
    </AccountModalsContext.Provider>;
};
const useAccountModals = () => {
  const context = useContext(AccountModalsContext);
  if (!context) {
    throw new Error('useAccountModals must be used within an AccountModalsProvider');
  }
  const openModalWithConversionPoint = useCallback((modal: AccountModalNames, conversionPoint: ModalConversionPoints, modalProps?: ModalProps, screen?: string) => {
    context.setCurrentModal(modal, screen, modalProps);

    // Extract tag if it exists in modalProps for backward compatibility
    const tag = modalProps?.tag;
    recordConversion({
      source: conversionPoints[conversionPoint],
      assetId: tag === null || tag === undefined ? undefined : Number(tag)
    });
  }, [context]);
  return {
    ...context,
    openModalWithConversionPoint
  };
};
export { AccountModalsProvider, useAccountModals };